import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule } from '@ngx-translate/core';
import { COUNTRY_PREFIXES, CountryPrefix } from '../utils/phone-prefix.utils';

@Component({
  selector: 'input-phone',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TranslateModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './input-phone.component.html',
  styles: [`
    .prefix-selector {
      width: auto;
      min-width: 90px;
      padding: 0 4px !important;
      margin-right: 4px;
      height: 100%;
      border-right: 1px solid #ccc;
      border-radius: 0;
    }
    .prefix-container {
      display: flex;
      align-items: center;
      gap: 6px;
    }
    .flag-icon {
      font-size: 18px;
    }
    .prefix-number {
      font-size: 14px;
      font-weight: 500;
    }
    .phone-prefix-menu {
      max-height: 300px;
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputPhoneComponent),
      multi: true,
    },
  ],
})
export class InputPhoneComponent implements ControlValueAccessor, OnInit {
  @Input() label = 'phone';
  @Input() required = false;
  @Input() readonly = false;
  @Input() formFieldClass: string = '';

  COUNTRY_PREFIXES = COUNTRY_PREFIXES;

  control = new FormControl('');
  selectedPrefix: CountryPrefix | null = null;

  onChange: any = () => { };
  onTouched: any = () => { };

  constructor() {
    // Default to Italy
    const italy = this.COUNTRY_PREFIXES.find(p => p.code === 'IT') || null;
    this.selectedPrefix = italy;

    this.control.valueChanges.subscribe(value => {
      this.updateModelValue();
    });
  }

  ngOnInit() {
    if (this.required) {
      this.control.setValidators(Validators.required);
    }
  }

  private updateModelValue() {
    const value = this.control.value || '';
    // Discard the initial '+' for the model value
    const numericValue = value.startsWith('+') ? value.substring(1) : value;
    this.onChange(numericValue);
    this.onTouched();
  }

  writeValue(value: any): void {
    if (value) {
      // Find matching prefix
      const matchingPrefix = this.COUNTRY_PREFIXES
        .filter(p => value.startsWith(p.prefix))
        .sort((a, b) => b.prefix.length - a.prefix.length)[0]; // Longest match

      if (matchingPrefix) {
        this.selectedPrefix = matchingPrefix;
        this.control.setValue('+' + value, { emitEvent: false });
      } else {
        this.control.setValue(value, { emitEvent: false });
      }
    } else {
      // If no value, still ensure the input starts with the current prefix
      if (this.selectedPrefix) {
        this.control.setValue('+' + this.selectedPrefix.prefix, { emitEvent: false });
      } else {
        this.control.setValue('', { emitEvent: false });
      }
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable();
  }

  onPrefixSelected(prefix: CountryPrefix) {
    if (!prefix) return;

    const oldPrefix = this.selectedPrefix;
    this.selectedPrefix = prefix;

    let currentValue = this.control.value || '';

    if (currentValue.startsWith('+')) {
      const oldPrefixMatch = oldPrefix || this.COUNTRY_PREFIXES
        .filter(p => currentValue.startsWith('+' + p.prefix))
        .sort((a, b) => b.prefix.length - a.prefix.length)[0];

      if (oldPrefixMatch) {
        const phoneWithoutPrefix = currentValue.substring(oldPrefixMatch.prefix.length + 1);
        this.control.setValue('+' + prefix.prefix + phoneWithoutPrefix);
      } else {
        this.control.setValue('+' + prefix.prefix);
      }
    } else {
      this.control.setValue('+' + prefix.prefix + currentValue);
    }
  }

  onPhoneInput(event: any) {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    const prefix = this.selectedPrefix;

    if (!prefix) {
      const numericValue = value.replace(/\D/g, '');
      this.control.setValue(numericValue);
      return;
    }

    const prefixStr = '+' + prefix.prefix;

    if (!value.startsWith(prefixStr)) {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.startsWith(prefix.prefix)) {
        value = '+' + digitsOnly;
      } else {
        value = prefixStr + digitsOnly;
      }
    } else {
      const rest = value.substring(prefixStr.length).replace(/\D/g, '');
      value = prefixStr + rest;
    }

    this.control.setValue(value, { emitEvent: true });
    input.value = value;
  }
}
